import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import c from 'classnames';
import { Url } from '@wix/da-url';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import {
  BiContextProvidedValue,
  BiData,
  DreamupInfoClickBiEvent,
} from '@wix/da-bi/pkg/events';
import DaDreamupLogoAndText from '@wix/da-ds/pkg/DaDreamupLogoAndText';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { DeviationLink } from '../../../../Duperbrowse';
import s from './CreateWithDreamupBanner.scss';

interface Props {
  deviation: PapiDeviation;
  author?: PapiUser;
  prompt?: string;
  promptHidden?: boolean;
  variationSource?: PapiDeviation;
  className?: string;
}

function getHighlightedCallToActionText(
  t: TFunction,
  isMobile: boolean,
  hasCoreMembership: boolean
) {
  if (hasCoreMembership && isMobile) {
    return t('deviation.details.dreamup.callToAction.link.text.core.mobile');
  } else if (hasCoreMembership && !isMobile) {
    return t('deviation.details.dreamup.callToAction.link.text.core.desktop');
  } else if (isMobile) {
    return t('deviation.details.dreamup.callToAction.link.text.free.mobile');
  } else {
    return t('deviation.details.dreamup.callToAction.link.text.free.desktop');
  }
}

const CallToActionBlock: React.FC<{ deviation: PapiDeviation }> = ({
  deviation,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { hasCoreMembership } = useContext(SessionContext);

  return (
    <PlainButtonOrLink
      className={s['call-to-action-block']}
      href={Url.dreamupLink({ deviationid: deviation.deviationId })}
      biData={BiData<DreamupInfoClickBiEvent>({
        evid: 923,
        typeid: BiContextProvidedValue,
        itemid: BiContextProvidedValue,
      })}
    >
      <DaDreamupLogoAndText className={s['logo']} view={'light'} />
      <div className={s['call-to-action-text-stack']}>
        {!isMobile && (
          <p className={s['call-to-action-forward-copy']}>
            {t('deviation.details.dreamup.callToAction.forwardCopy')}
          </p>
        )}
        <span className={s['call-to-action-highlighted-text']}>
          {getHighlightedCallToActionText(t, isMobile, hasCoreMembership)}
        </span>
      </div>
    </PlainButtonOrLink>
  );
};
CallToActionBlock.displayName = 'CallToActionBlock';

const InfoEntry: React.FC<{
  left: React.ReactNode;
  right: React.ReactNode;
  highlightValue?: boolean;
}> = ({ left, right, highlightValue }) => (
  <p className={s['info-entry']}>
    <span className={s['info-entry-preface']}>{left}:</span>
    <span
      className={c(s['info-entry-value'], highlightValue && s['highlight'])}
    >
      {right}
    </span>
  </p>
);
InfoEntry.displayName = 'InfoEntry';

const CreateWithDreamupBanner: React.FC<Props> = ({
  deviation,
  author,
  prompt,
  promptHidden,
  variationSource,
  className,
}) => {
  const { t } = useTranslation();
  return (
    <div className={c(s['root'], className)}>
      {(prompt || promptHidden) && author && (
        <InfoEntry
          left={t('deviation.details.dreamup.prompt.preface')}
          right={
            promptHidden
              ? t('deviation.details.dreamup.prompt.hidden', {
                  username: author.username,
                })
              : prompt
          }
        />
      )}
      {variationSource && (
        <InfoEntry
          left={t('deviation.details.dreamup.variation.preface')}
          right={
            <DeviationLink deviation={variationSource} canLinkToStash>
              {t('deviation.details.dreamup.variation.text')}
            </DeviationLink>
          }
          highlightValue
        />
      )}
      <div className={s['call-to-action-and-preface']}>
        {
          // No prompt means this wasn't created with Dreamup, so we drop the "created with" preface
          (prompt || promptHidden) && (
            <p className={s['call-to-action-preface']}>
              {t('deviation.details.dreamup.callToAction.preface')}
            </p>
          )
        }
        <CallToActionBlock deviation={deviation} />
      </div>
    </div>
  );
};

CreateWithDreamupBanner.displayName = 'CreateWithDreamupBanner';

export default CreateWithDreamupBanner;
